/**
 * Hook de comprobantes de una asignación (US-2.4, TG-53).
 *
 * Lista metadatos (`GET .../receipts`), sube un archivo (`POST` multipart campo
 * `file`) y refresca tras la mutación. El listado NO incluye `downloadUrl`: la
 * descarga resuelve el detalle (`GET .../receipts/{id}`) que sí la trae firmada.
 */
import { useCallback, useEffect, useState } from 'react';
import type { CustomerReceipt, ReceiptAssignmentType } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { asignacionesApi } from '../api/asignacionesApi';

export interface UseReceiptsResult {
  receipts: CustomerReceipt[];
  loading: boolean;
  error: string | null;
  uploading: boolean;
  /** `true` mientras se resuelve la URL firmada de una descarga. */
  downloading: boolean;
  refresh: () => void;
  /** Sube el comprobante y refresca el listado; propaga el error para la vista. */
  upload: (file: File) => Promise<void>;
  /** Resuelve `downloadUrl` firmada del comprobante (detalle del contrato). */
  resolveDownloadUrl: (receiptId: string) => Promise<string | null>;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar los comprobantes.';
}

export function useReceipts(
  customerId: string | undefined,
  type: ReceiptAssignmentType,
  assignmentId: string | undefined,
  enabled: boolean,
): UseReceiptsResult {
  const [receipts, setReceipts] = useState<CustomerReceipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled || !customerId || !assignmentId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    asignacionesApi
      .listReceipts(customerId, type, assignmentId)
      .then((result) => {
        if (!cancelled) setReceipts(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(caught));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, customerId, type, assignmentId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  const upload = useCallback(
    async (file: File) => {
      if (!customerId || !assignmentId) {
        throw new Error('Falta la asignación para subir el comprobante.');
      }
      setUploading(true);
      try {
        await asignacionesApi.uploadReceipt(customerId, type, assignmentId, file);
        setReloadKey((key) => key + 1);
      } finally {
        setUploading(false);
      }
    },
    [customerId, type, assignmentId],
  );

  const resolveDownloadUrl = useCallback(
    async (receiptId: string) => {
      if (!customerId || !assignmentId) return null;
      setDownloading(true);
      try {
        const receipt = await asignacionesApi.getReceipt(
          customerId,
          type,
          assignmentId,
          receiptId,
        );
        return receipt.downloadUrl ?? null;
      } finally {
        setDownloading(false);
      }
    },
    [customerId, type, assignmentId],
  );

  return { receipts, loading, error, uploading, downloading, refresh, upload, resolveDownloadUrl };
}
